// src/components/ProtectedSuperRoute.tsx
import React, { ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useUserContext } from "../contexts/UserContext";
import LoadingInline from "../components/LoadingInline";
import { apiGet } from "../utils/apiClient";

type Props = {
    children: ReactNode;
};

export default function ProtectedSuperRoute({ children }: Props) {
    const user = useUserContext();
    const [fallbackAllowed, setFallbackAllowed] = useState<boolean | null>(null);

    const okByContext =
        user.superAdmin === true ||
        user.superAdminGlobal === true ||
        (Array.isArray((user as any).superAdminTenants) && (user as any).superAdminTenants.length > 0);

    useEffect(() => {
        if (user.loading) return;
        if (okByContext) {
            setFallbackAllowed(true);
            return;
        }

        let alive = true;
        (async () => {
            try {
                const res = await apiGet("/api/me");
                if (!alive) return;
                if (!res.ok) {
                    setFallbackAllowed(false);
                    return;
                }
                const me: any = await res.json().catch(() => ({}));
                const ok =
                    me?.superAdmin === true ||
                    me?.superAdminGlobal === true ||
                    (Array.isArray(me?.superAdminTenants) && me.superAdminTenants.length > 0);
                setFallbackAllowed(ok);
            } catch {
                if (alive) setFallbackAllowed(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [user.loading, okByContext]);

    if (user.loading) return <LoadingInline />;
    if (fallbackAllowed === null) return <LoadingInline />;

    if (!fallbackAllowed) return <Navigate to="/app" replace />;

    return <>{children}</>;
}
